import { useState, useEffect, useMemo } from "react";
import { formatDateTR } from "@/utils/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, TrendingUp, CreditCard, Package, Calendar, DollarSign, CheckCircle, Clock, List, AlertCircle } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

export default function EmployeeCommissionReportModal({ isOpen, onClose, employee, businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports", "packages"]);
  const MONTHS = MONTHS_TR.map((m, i) => t(`auth:month${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}`) || m);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (isOpen && employee) {
      fetchReport();
    }
  }, [isOpen, employee, selectedMonth, selectedYear]);

  const fetchReport = async () => {
    if (!employee?.employee_id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/employees/${employee.employee_id}/my-commissions?month=${selectedMonth}&year=${selectedYear}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error(t("reports:toast_raporYuklenemedi"));
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return formatDateTR(dateStr);
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  // Calculate payment status using FIFO method
  const calculatePaymentStatus = (details, paidAmount) => {
    if (!details || details.length === 0) return [];
    
    // Sort by date ascending (FIFO - first in, first out)
    const sortedDetails = [...details].sort((a, b) => {
      const dateA = a.date || a.lesson_date || '';
      const dateB = b.date || b.lesson_date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.lesson_time || '';
      const timeB = b.lesson_time || '';
      return timeA.localeCompare(timeB);
    });
    
    let remainingPaid = paidAmount;
    
    return sortedDetails.map(item => {
      const commission = item.commission || 0;
      let paymentStatus = 'pending'; // bekliyor
      let paidAmountForThis = 0;
      
      if (remainingPaid >= commission) {
        // Fully paid
        paymentStatus = 'paid';
        paidAmountForThis = commission;
        remainingPaid -= commission;
      } else if (remainingPaid > 0) {
        // Partially paid
        paymentStatus = 'partial';
        paidAmountForThis = remainingPaid;
        remainingPaid = 0;
      }
      
      return {
        ...item,
        paymentStatus,
        paidAmountForThis,
        remainingAmount: commission - paidAmountForThis
      };
    });
  };

  // Process all details with payment status
  const processedData = useMemo(() => {
    if (!reportData) return { cardDetails: [], packageDetails: [], allDetails: [] };
    
    const paidAmount = reportData.paid_amount || 0;
    
    // Combine all details and calculate payment status
    const allRawDetails = reportData.details || [...(reportData.card_details || []), ...(reportData.package_details || [])];
    const allDetailsWithStatus = calculatePaymentStatus(allRawDetails, paidAmount);
    
    // Split back into card and package
    const cardDetails = allDetailsWithStatus.filter(d => d.payment_type === "card");
    const packageDetails = allDetailsWithStatus.filter(d => d.payment_type !== "card");
    
    return {
      cardDetails,
      packageDetails,
      allDetails: allDetailsWithStatus
    };
  }, [reportData]);

  const { cardDetails, packageDetails, allDetails } = processedData;
  const totalSessions = reportData?.lesson_count || reportData?.total_sessions || 0;

  const renderPaymentStatusBadge = (item) => {
    if (item.paymentStatus === 'paid') {
      return (
        <Badge className="bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Ödendi
        </Badge>
      );
    } else if (item.paymentStatus === 'partial') {
      return (
        <Badge className="bg-orange-100 text-orange-700">
          <AlertCircle className="w-3 h-3 mr-1" />
          Kısmi Ödeme
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3 mr-1" />
          Bekliyor
        </Badge>
      );
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Prim Raporu - {employee.name} {employee.surname}
          </DialogTitle>
        </DialogHeader>

        {/* Month/Year Selector */}
        <div className="flex gap-3 mb-4">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[140px] rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month, index) => (
                <SelectItem key={index} value={String(index + 1)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-navy" />
          </div>
        ) : reportData ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-0 shadow-sm bg-blue-50">
                <CardContent className="p-4">
                  <div className="text-sm text-blue-600 mb-1">{t("members:totalSessions")}</div>
                  <div className="text-2xl font-bold text-blue-700">{totalSessions}</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm bg-green-50">
                <CardContent className="p-4">
                  <div className="text-sm text-green-600 mb-1">{t("reports:totalCommission2")}</div>
                  <div className="text-2xl font-bold text-green-700">{formatCurrency(reportData.total_commission)}</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm bg-purple-50">
                <CardContent className="p-4">
                  <div className="text-sm text-purple-600 mb-1">{t("common:paidAmount")}</div>
                  <div className="text-2xl font-bold text-purple-700">{formatCurrency(reportData.paid_amount)}</div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm bg-orange-50">
                <CardContent className="p-4">
                  <div className="text-sm text-orange-600 mb-1">{t("common:pending")}</div>
                  <div className="text-2xl font-bold text-orange-700">{formatCurrency(reportData.pending_amount)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Tabs */}
            <Tabs defaultValue="all" className="mt-4">
              <TabsList className="grid w-full grid-cols-3 rounded-xl">
                <TabsTrigger value="all" className="rounded-xl flex items-center gap-2">
                  <List className="w-4 h-4" />
                  {t("common:allCount")} ({allDetails.length})
                </TabsTrigger>
                <TabsTrigger value="card" className="rounded-xl flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {t("common:cardLabel")} ({cardDetails.length})
                </TabsTrigger>
                <TabsTrigger value="package" className="rounded-xl flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  {t("packages:package")} ({packageDetails.length})
                </TabsTrigger>
              </TabsList>
              
              {/* All Details Tab */}
              <TabsContent value="all" className="mt-4">
                {allDetails.length > 0 ? (
                  <div className="rounded-xl border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('common:date')}</TableHead>
                          <TableHead>{t("common:member")}</TableHead>
                          <TableHead>{t("common:typeLabel")}</TableHead>
                          <TableHead>{t('common:detail')}</TableHead>
                          <TableHead>{t("common:rate")}</TableHead>
                          <TableHead className="text-right">{t("reports:commissionLabel")}</TableHead>
                          <TableHead>{t('common:status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allDetails.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {formatDate(item.date || item.lesson_date)}
                            </TableCell>
                            <TableCell>{item.member_name || "-"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {item.payment_type === "card" ? t("common:cardLabel") : t('packages:package')}
                              </Badge>
                            </TableCell>
                            <TableCell>{item.card_name || item.package_name || item.training_type || "-"}</TableCell>
                            <TableCell>
                              {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {formatCurrency(item.commission)}
                            </TableCell>
                            <TableCell>
                              {renderPaymentStatusBadge(item)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    {t("reports:noCommissionThisPeriod")}
                  </div>
                )}
              </TabsContent>
              
              {/* Card Details Tab */}
              <TabsContent value="card" className="mt-4">
                {cardDetails.length > 0 ? (
                  <div className="rounded-xl border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('common:date')}</TableHead>
                          <TableHead>{t("common:member")}</TableHead>
                          <TableHead>{t("common:cardLabel")}</TableHead>
                          <TableHead>{t("common:rate")}</TableHead>
                          <TableHead className="text-right">{t("reports:commissionLabel")}</TableHead>
                          <TableHead>{t('common:status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cardDetails.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {formatDate(item.date || item.lesson_date)}
                            </TableCell>
                            <TableCell>{item.member_name || "-"}</TableCell>
                            <TableCell>{item.card_name || "-"}</TableCell>
                            <TableCell>
                              {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {formatCurrency(item.commission)}
                            </TableCell>
                            <TableCell>
                              {renderPaymentStatusBadge(item)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    {t("reports:noCardCommThisPeriod")}
                  </div>
                )}
              </TabsContent>
              
              {/* Package Details Tab */}
              <TabsContent value="package" className="mt-4">
                {packageDetails.length > 0 ? (
                  <div className="rounded-xl border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('common:date')}</TableHead>
                          <TableHead>{t("common:member")}</TableHead>
                          <TableHead>{t("packages:package")}</TableHead>
                          <TableHead>{t("common:rate")}</TableHead>
                          <TableHead className="text-right">{t("reports:commissionLabel")}</TableHead>
                          <TableHead>{t('common:status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {packageDetails.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {formatDate(item.date || item.lesson_date)}
                            </TableCell>
                            <TableCell>{item.member_name || "-"}</TableCell>
                            <TableCell>{item.package_name || "-"}</TableCell>
                            <TableCell>
                              {item.rate_type === "percentage" ? `%${item.rate}` : formatCurrency(item.rate)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {formatCurrency(item.commission)}
                            </TableCell>
                            <TableCell>
                              {renderPaymentStatusBadge(item)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    {t("packages:noPackageCommThisPeriod")}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="text-center py-8 text-grey">
            {t("reports:reportDataNotFound")}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>
            {t("common:close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
